import api from "../api";
import createAppAsyncThunk from "../createAppAsyncThunk";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { NETWORK_ERROR_MESSAGE } from "../../utils/getErrorMessage";
import {
  QUOTE_REQUESTED,
  QUOTE_RECEIVED,
  QUOTE_FAILED,
  CLEAR_URL,
  QUOTES_REQUESTED,
  QUOTES_RECEIVED,
  QUOTES_FAILED,
  CLEAR_URLS,
  GET_FOLDERS,
  GET_PICTURES,
  CREATE_FOLDER,
  CLEAR_FOLDER,
  SET_PATH,
} from "./ImageTypes";
import { asCaught } from "../caughtError";

async function postImage(data: string, path: string): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch("/api/image/upload", {
      method: "POST",
      body: JSON.stringify({ data, path }),
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
  } catch (err) {
    if (err instanceof TypeError) throw new Error(NETWORK_ERROR_MESSAGE);
    throw err;
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to upload image");
  }
  return response.json();
}

const uploadOne = createAppAsyncThunk(
  "images/upload",
  async (arg: { data: string; path: string }, { dispatch }) => {
    dispatch({ type: QUOTE_REQUESTED });
    const body = await postImage(arg.data, arg.path);
    dispatch({ type: QUOTE_RECEIVED, payload: body });
    return body;
  }
);

// Two arguments, and the caller needs the URL or a thrown Error (SCRUM-815).
// createAsyncThunk takes one argument and resolves to the action, so this
// wrapper keeps the old call shape.
export const uploadImageAction = (base64EncodedImage, path) => async (dispatch) => {
  try {
    return await dispatch(uploadOne({ data: base64EncodedImage, path })).unwrap();
  } catch (error) {
    const failed = new Error((error as { message?: string })?.message || "Failed to upload image");
    dispatch({ type: QUOTE_FAILED, payload: failed });
    throw failed;
  }
};

// SCRUM-1594: set (or clear, with "") the alt stored on an existing Cloudinary
// resource. Same fetch + response.ok pattern as uploadImageAction above; resolves to
// the server body `{ public_id, context }` and re-throws on a non-2xx / network error
// so the panel can show the reason instead of a false "Saved".
export const updateImageAltAction = (public_id, alt) => async (dispatch) => {
  try {
    const response = await fetch("/api/image/updateAlt", {
      method: "POST",
      body: JSON.stringify({ public_id, alt }),
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData?.errors?.[0]?.msg || errorData?.message || "Failed to update alt text"
      );
    }
    return await response.json();
  } catch (error) {
    dispatch({
      type: GET_ERRORS,
      payload: { message: asCaught(error)?.message },
    });
    throw error;
  }
};

export const clearUrl = () => (dispatch) => {
  dispatch({
    type: CLEAR_URL,
  });
};


const uploadMany = createAppAsyncThunk(
  "images/uploadMany",
  async (arg: { index: number; data: string; path: string }, { dispatch }) => {
    dispatch({ type: QUOTES_REQUESTED });
    const body = await postImage(arg.data, arg.path);
    dispatch({ type: QUOTES_RECEIVED, payload: { index: arg.index, data: body } });
    return body;
  }
);

export const uploadImagesAction = (index, base64EncodedImage, path) => async (dispatch) => {
  try {
    return await dispatch(
      uploadMany({ index, data: base64EncodedImage, path })
    ).unwrap();
  } catch (error) {
    const failed = new Error((error as { message?: string })?.message || "Failed to upload image");
    dispatch({ type: QUOTES_FAILED, payload: failed.message });
    throw failed;
  }
};


export const clearUrls = () => (dispatch) => {
  dispatch({
    type: CLEAR_URLS,
  });
};

// Get Folders — also RESOLVES to res.data so a caller can key the result to the
// path it fetched (used by useMediaBrowser for race-free per-path caching). The
// GET_FOLDERS dispatch is unchanged; on error resolves to [] (and dispatches GET_ERRORS).
export const getFolders = (public_id) => (dispatch) => {
  return api
    .post(
      "/api/image/searchFolder",
      public_id
    )
    .then((res) => {
      dispatch({
        type: GET_FOLDERS,
        payload: res.data,
      });
      return res.data;
    })
    .catch((err) => {
      dispatch({
        type: GET_ERRORS,
        payload: err.response?.data, // the body ({errors:[{msg}]}), not the whole
        // axios response — matches addFolder and what getErrorMessage expects, so
        // a real failure shows its message instead of the generic fallback.
      });
      return undefined; // signal failure (not an empty result) so the caller does not cache it
    });
};
// Get Pictures — resolves to res.data (see getFolders); [] on error.
export const getPictures = (folder) => (dispatch) => {
  return api
    .post(
      "/api/image/getImagesByFolder",
      folder
    )
    .then((res) => {
      dispatch({
        type: GET_PICTURES,
        payload: res.data,
      });
      return res.data;
    })
    .catch((err) => {
      dispatch({
        type: GET_ERRORS,
        payload: err.response?.data, // body, not the whole axios response (see getFolders)
      });
      return undefined; // signal failure (not an empty result) so the caller does not cache it
    });
};

//Add Folder — resolves to the created folder doc (res.data) so the caller can
// prepend it to the current folder listing; null on error. CREATE_FOLDER dispatch unchanged.
export const addFolder = (public_id) => (dispatch) => {
  //public_id : p2cms/FR     ----> creating folder FR in p2cms folder
  return api
    .post("/api/image/createFolder", {
      public_id: public_id,
    })
    .then((res) => {
      dispatch({
        type: CREATE_FOLDER,
        payload: res.data,
      });
      return res.data;
    })

    .catch((err) => {
      // SCRUM-1317: was `err.response.data` unguarded. axios rejects with no
      // `response` on a network/DNS/timeout failure, so this catch threw its own
      // TypeError -- escaping the chain as an unhandled rejection, dispatching
      // nothing, and skipping the `return null` this thunk's own comment promises.
      dispatch({
        type: GET_ERRORS,
        payload: err?.response?.data ?? { message: err?.message },
      });
      return null;
    });
};
export const clearFolder = () => (dispatch) => {
  dispatch({
    type: CLEAR_FOLDER,
  });
};

export const setCloudinaryPath = (path) => (dispatch) => {
  dispatch({
    type: SET_PATH,
    payload: path,
  });
};

export const clearCloudinaryPath = () => (dispatch) => {
  dispatch({
    type: SET_PATH,
    payload: "",
  });
}
